import { FetchStatus } from '@flatsy/types/FetchStatus'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { getApplicants } from 'src/redux/slices/Applicant/Action'
import { getApplication, selectApplicationState } from 'src/redux/slices/Application'
import { fetchQualification } from 'src/redux/slices/QualificationConfig'
import { selectQualificationConfig } from 'src/redux/slices/QualificationConfig/Selector'
import { selectApplicantsState } from 'src/redux/slices/Applicant/Selector'
import { AppDispatch } from 'src/utils/AppDispatch'
import { useApplicantsSelector } from 'src/hooks/ApplicantsSelector'
import { Applicant, PublicApplicationData } from '@flatsy/types/Applications'
import { useAsset } from '../Asset'
import { HookReturn } from './Type'

export const useApplicationDetails = (): HookReturn => {
  const dispatch: AppDispatch = useDispatch()
  const applicationState = useSelector(selectApplicationState)
  const qualification = useSelector(selectQualificationConfig)
  const applicantsState = useSelector(selectApplicantsState)
  const applicants = useApplicantsSelector()

  const { applicationId, id } = useParams<{
    id: string
    applicationId: string
    userReference?: string
  }>()
  const { asset, requestStatus } = useAsset(id || applicationState.application.asset || '')

  const getApplicantsAction = async (directApplicationId: string): Promise<false | Applicant[]> => {
    try {
      const action = await dispatch(getApplicants(directApplicationId))
      return unwrapResult(action)
    } catch (err) {
      return false
    }
  }

  const getApplicationAction = async (
    directApplicationId: string
  ): Promise<false | PublicApplicationData> => {
    try {
      const action = await dispatch(getApplication(directApplicationId))

      return unwrapResult(action)
    } catch (err) {
      return false
    }
  }

  const retrieveApplicationData = async (
    directApplicationId?: string
  ): Promise<{
    applicants: Applicant[] | false
    application: PublicApplicationData | false
  }> => {
    try {
      if (applicationId || directApplicationId) {
        const application = await getApplicationAction(directApplicationId || applicationId)
        const applicants = await getApplicantsAction(directApplicationId || applicationId)
        return {
          application,
          applicants,
        }
      }
      return {
        applicants: false,
        application: false,
      }
    } catch (err) {
      return {
        applicants: false,
        application: false,
      }
    }
  }

  const fetchApplicationQualification = async () => {
    try {
      const action = await dispatch(
        fetchQualification(applicationState.application.asset || asset?.url_id || '')
      )
      unwrapResult(action)
    } catch (err) {
      //
    }
  }

  useEffect(() => {
    /**
     * @description get public application in redux store
     */
    if (applicationId && applicationState.getStatus !== FetchStatus.SUCCEEDED) {
      retrieveApplicationData()
    }
  }, [applicationId])

  useEffect(() => {
    /**
     * @description fetch qualification when asset url_id is already retrieve and qualification is not already fetched
     */
    if (qualification.status === FetchStatus.IDLE) {
      if (
        (applicationState.application.asset && applicationState.application.asset !== '') ||
        (asset && asset.url_id)
      ) {
        fetchApplicationQualification()
      }
    }
  }, [applicationState.application.asset, asset, qualification.status])

  return {
    application: applicationState,
    applicationStatus: applicationState.getStatus,
    asset,
    assetRequestStatus: requestStatus,
    fetch: retrieveApplicationData,
    applicants,
    applicantsStatus: applicantsState.fetchStatus,
    qualification: qualification.config,
    qualificationStatus: qualification.status,
  }
}
